import { CustomButton } from '../ui';
import React, { Fragment, useState, useEffect } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import getCroppedImg from '@/helpers/cropImage';
import { ConfigProvider, Flex, Slider } from 'antd';

interface Props {
  imageToCrop: string;
  setImageToCrop: React.Dispatch<React.SetStateAction<string>>;
  setAvatar: React.Dispatch<React.SetStateAction<string>>;
  onCropComplete?: (croppedBlobUrl: string) => void;
  // When true (logo uploads), the crop box matches the image's own aspect ratio
  // so the entire logo is captured by default instead of a forced 1:1 crop.
  fitWholeImage?: boolean;
}

export default function ImageCropper({
  imageToCrop,
  setImageToCrop,
  setAvatar,
  onCropComplete,
  fitWholeImage = false,
}: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedPixels, setCroppedPixels] = useState<Area>();
  const [zoom, setZoom] = useState(fitWholeImage ? 1 : 1.2);
  // Logos: derive the crop-box aspect from the image's natural dimensions so the
  // whole logo fits by default. Photos keep the square (1:1) box.
  const [aspect, setAspect] = useState(1);

  useEffect(() => {
    if (!fitWholeImage || !imageToCrop) return;
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = imageToCrop;
  }, [fitWholeImage, imageToCrop]);

  const onCropCompleteHandler = async (
    _croppedArea: Area,
    croppedAreaPixels: Area,
  ) => {
    setCroppedPixels(croppedAreaPixels);
  };

  const onCrop = async () => {
    if (!croppedPixels) return;
    const croppedImage = await getCroppedImg(imageToCrop, croppedPixels);
    const blobUrl = croppedImage as string;

    setAvatar(blobUrl);
    setImageToCrop('');

    // Notify parent so it can mark dirty + store blob url reference
    onCropComplete?.(blobUrl);
  };

  return (
    <Fragment>
      {
        // @ts-ignore
        <Cropper
          image={imageToCrop}
          crop={crop}
          aspect={aspect}
          zoom={zoom}
          minZoom={0.2}
          maxZoom={3}
          restrictPosition={false}
          onZoomChange={setZoom}
          style={{
            containerStyle: {
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1000,
              backdropFilter: 'blur(10px)',
            },
          }}
          onCropChange={setCrop}
          onCropComplete={onCropCompleteHandler}
        />
      }
      <Flex
        style={{
          position: 'fixed',
          bottom: '40px',
          left: '50%',
          zIndex: 1001,
          transform: 'translateX(-50%)',
        }}
        justify="center"
        align="center"
        vertical
        gap={16}
      >
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#2A4BC2',
            },
          }}
        >
          <Slider
            min={0.2}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(value) => setZoom(value)}
            className={'custom-slider'}
            style={{
              width: 200,
            }}
          />
        </ConfigProvider>
        <CustomButton type={'primary'} onClick={onCrop} text={'Crop Image'} />
      </Flex>
    </Fragment>
  );
}
